import * as React from 'react';
import { Box, Grid, Typography } from '@mui/material';
import { useForm } from 'react-hook-form';
import { useSnackbar } from 'notistack';
import dayjs from 'dayjs';
import { LoadingButton } from '@mui/lab';

import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import AssignmentReturnOutlinedIcon from '@mui/icons-material/AssignmentReturnOutlined';
import PhoneInTalkOutlinedIcon from '@mui/icons-material/PhoneInTalkOutlined';
import SummarizeOutlinedIcon from '@mui/icons-material/SummarizeOutlined';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';

import { apiConstants, getGeneric, patchGeneric, postGeneric } from '../../../common/constants';
import { FormProvider } from '../../../common/hook-form';
import RHFDateTimeField from '../../../common/hook-form/RHFDateTimeField';
import ConfigList from '../../../components/QcConfig/ConfigList';
import Loader from '../../../common/Loader';
import PageTitle from '../../../components/PageTitle';
import InvoiceValueOnZero from './InvoiceValueOnZero';
import ShopifyFulfillmentTrigger from './ShopifyFulfillmentTrigger';

import { C, SX, TYPE } from '../shared/tokens';
import ConfigSection from '../shared/ConfigSection';
import ConfigRow, { ConfigToggle } from '../shared/ConfigRow';

// ─────────────────────────────────────────────────────────────────────────────
//  Merchant configuration.
//
//  This tab used to render twelve MUI data grids -- one grid per config object,
//  each showing a SINGLE row -- inside four hand-rolled collapsible sections,
//  with three different save patterns and no indication of which settings were
//  actually set. A grid is the wrong shape for one record.
//
//  Same endpoints, same fields, same permissions. What changed is that a setting
//  now reads as a setting: name, plain-English help, current value, and -- where
//  the backend actually records it (modifiedByUser / lastModifiedDate) -- who
//  changed it last.
//
//  Editable here: serviceability, pickup cut-off, automated reports, the
//  zero-invoice-value substitution, and Shopify fulfillment timing. Everything else is read-only in this tab and
//  always was; the labels now say so instead of implying otherwise.
// ─────────────────────────────────────────────────────────────────────────────

const fmtDate = (v) => {
    if (!v) return null;
    try {
        return dayjs(v).format('D MMM YYYY');
    } catch { return null; }
};

const auditOf = (o) => (o?.modifiedByUser || o?.createdByUser)
    ? { by: o.modifiedByUser || o.createdByUser, at: fmtDate(o.lastModifiedDate || o.createdDate) }
    : null;

const yesNo = (v) => (v ? 'Yes' : 'No');
const onOff = (v) => (v ? 'On' : 'Off');
const stateOf = (v) => (v ? 'set' : 'unset');

function Section({ children }) {
    return <Box sx={{ pb: 0.5 }}>{children}</Box>;
}

// A group of related read-only values, shown as a compact definition list
// rather than a one-row data grid.
function ReadOnlyGroup({ rows }) {
    return (
        <>
            {rows.filter((r) => r).map((r) => (
                <ConfigRow key={r.label} dense label={r.label} help={r.help} value={r.value ?? '—'} state={r.state} audit={r.audit} />
            ))}
        </>
    );
}

export default function Configs({ data, id, setReload }) {
    const { enqueueSnackbar } = useSnackbar();
    const [cfg, setCfg] = React.useState(null);
    const [reload2, setReload2] = React.useState(0);
    const [savingTime, setSavingTime] = React.useState(false);
    const [busy, setBusy] = React.useState(false);

    React.useEffect(() => {
        getGeneric(`${apiConstants.MERCHANT_DETAILS}/${id}`)
            .then((res) => setCfg(res?.data || {}))
            .catch((err) => console.error(err));
    }, [id, reload2]);

    const mis = cfg?.misConfig;

    // ── Serviceability (lives on the merchant doc, not merchant_config) ──
    const toggleServiceability = async (next) => {
        setBusy(true);
        try {
            await patchGeneric(apiConstants.TOGGLE_SERVICABLITY, { id, checkServiceability: next });
            setReload((r) => r + 1);
        } finally {
            setBusy(false);
        }
    };

    // ── Automated MIS reports ──
    const setMisFlag = async (which, next) => {
        const payload = {
            monthlyEnabled: which === 'monthly' ? next : !!mis?.monthlyEnabled,
            dailyEnabled: which === 'daily' ? next : !!mis?.dailyEnabled,
            recievedByEmail: mis?.recievedByEmail,
            recievedByBccEmail: ['ProshipTechTeam@prozo.com'],
        };
        await postGeneric(`${apiConstants.MERCHANT_DETAILS}/${id}/mis`, payload);
        setReload2((r) => r + 1);
    };

    // ── Pickup cut-off time ──
    const methods = useForm({ defaultValues: { pickupCutOffTime: null } });
    const { handleSubmit, reset, setValue } = methods;

    React.useEffect(() => {
        const t = cfg?.eddConfig?.pickupTimeConfig;
        if (t?.hour !== undefined && t?.minutes !== undefined) {
            setValue('pickupCutOffTime', dayjs().hour(t.hour).minute(t.minutes).second(0).millisecond(0));
        }
    }, [cfg, setValue]);

    const saveCutOff = (form) => {
        setSavingTime(true);
        postGeneric(`${apiConstants.MERCHANT_QC_LIST}/edd/pickupTime/${id}`, {
            hour: form?.pickupCutOffTime?.hour() || 0,
            minutes: form?.pickupCutOffTime?.minute() || 0,
        })
            .then(() => {
                enqueueSnackbar('Pickup cut-off time saved', { variant: 'success' });
                setReload2((r) => r + 1);
            })
            .catch((err) => enqueueSnackbar(
                err?.response?.data?.message || 'Could not save the pickup cut-off time. Try again.',
                { variant: 'error' },
            ))
            .finally(() => setSavingTime(false));
    };

    const cutOff = cfg?.eddConfig?.pickupTimeConfig;
    const cutOffLabel = cutOff
        ? dayjs().hour(cutOff.hour).minute(cutOff.minutes).format('hh:mm A')
        : null;

    // COD<->prepaid conversion lives on MerchantConfig.orderConversionCharges
    // (verified against OrderConversionCharges in MerchantConfig.java).
    const conv = cfg?.orderConversionCharges || {};
    const insurance = cfg?.insuranceEnabled || {};
    const returnCfg = cfg?.returnConfig || {};
    const shippingConfigs = cfg?.shippingConfig?.configs || [];

    return (
        <>
            {busy && <Loader />}
            <PageTitle>Configuration{data?.name ? ` / ${data.name}` : ''}</PageTitle>

            {/* ── Booking & serviceability ─────────────────────────────── */}
            <ConfigSection
                title="Booking & serviceability"
                subtitle="Checks and cut-offs applied when an order is handed to a courier."
                icon={<LocalShippingOutlinedIcon />}
                defaultOpen
                state={data?.checkServiceability ? 'set' : 'unset'}
            >
                <Section>
                    <ConfigToggle
                        label="Check serviceability before booking"
                        help="Verify the courier actually serves the destination pincode before handing over the shipment. With this off, shipments are booked directly and may be rejected later."
                        checked={!!data?.checkServiceability}
                        onChange={toggleServiceability}
                    />

                    <ConfigRow
                        label="Pickup cut-off time"
                        help="Forward orders placed after this time roll to the next day's pickup."
                        state={cutOff ? 'set' : 'unset'}
                        value={cutOffLabel}
                        control={
                            <FormProvider methods={methods} onSubmit={handleSubmit(saveCutOff)}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Box sx={{ width: 150 }}>
                                        <RHFDateTimeField required name="pickupCutOffTime" mode="time" label="" />
                                    </Box>
                                    <LoadingButton loading={savingTime} size="small" variant="contained" type="submit" sx={SX.cta}>
                                        Save
                                    </LoadingButton>
                                    <LoadingButton size="small" variant="outlined" onClick={() => reset()} sx={SX.ctaGhost}>
                                        Cancel
                                    </LoadingButton>
                                </Box>
                            </FormProvider>
                        }
                    />
                </Section>

                {/* Zero invoice_value substitution -- live on master, kept full-width
                    here because its forward-direction warning needs the room. */}
                <Box sx={{ py: 1.5, borderTop: `1px solid ${C.lineSoft}` }}>
                    <InvoiceValueOnZero id={id} config={cfg} setReload={setReload2} />
                </Box>
            </ConfigSection>

            {/* ── Payments & insurance ─────────────────────────────────── */}
            <ConfigSection
                title="Payments & insurance"
                subtitle="COD conversion and shipment cover. Set by the finance team."
                icon={<PaymentsOutlinedIcon />}
                state={conv?.codToPrepadEnabled || insurance?.insuranceEnabled ? 'set' : 'unset'}
                badge="Read-only here"
            >
                <ReadOnlyGroup rows={[
                    { label: 'Convert COD to prepaid', help: 'Ask the buyer to prepay instead of paying on delivery, above the amount below.', value: onOff(conv?.codToPrepadEnabled), state: stateOf(conv?.codToPrepadEnabled), audit: auditOf(conv) },
                    conv?.codToPrepad != null && { label: 'COD → prepaid threshold', help: 'Order value above which conversion is offered.', value: `₹${conv.codToPrepad}` },
                    { label: 'Convert prepaid to COD', help: 'Allow a prepaid order to be switched to cash on delivery.', value: onOff(conv?.prepadToCodEnabled), state: stateOf(conv?.prepadToCodEnabled) },
                    conv?.prepadToCod != null && { label: 'Prepaid → COD threshold', value: `₹${conv.prepadToCod}` },
                    { label: 'SMS the buyer on conversion', value: yesNo(conv?.sendSMS), state: stateOf(conv?.sendSMS) },
                    { label: 'WhatsApp the buyer on conversion', value: yesNo(conv?.sendWhatsapp), state: stateOf(conv?.sendWhatsapp) },
                    { label: 'Insurance', help: 'Whether shipments for this merchant are insured.', value: onOff(insurance?.insuranceEnabled), state: stateOf(insurance?.insuranceEnabled), audit: auditOf(insurance) },
                    { label: 'Insure automatically', help: 'Cover every qualifying order without a manual step.', value: onOff(insurance?.autoEnabled), state: stateOf(insurance?.autoEnabled) },
                    insurance?.autoEnabledForMinInvoiceValue != null && { label: 'Auto-insure above', help: 'Invoice value at which cover is applied automatically.', value: `₹${insurance.autoEnabledForMinInvoiceValue}` },
                    insurance?.flatCharges != null && { label: 'Flat insurance charge', value: `₹${insurance.flatCharges}` },
                    insurance?.percentOfInvoice != null && { label: 'Insurance as % of invoice', value: `${insurance.percentOfInvoice}%` },
                ]} />
            </ConfigSection>

            {/* ── Shipping & returns ───────────────────────────────────── */}
            <ConfigSection
                title="Shipping & returns"
                subtitle="Label content and how returns are communicated to the buyer."
                icon={<AssignmentReturnOutlinedIcon />}
                state={returnCfg?.isActive ? 'set' : 'unset'}
                badge={shippingConfigs.length ? `${shippingConfigs.length} shipping keys` : undefined}
            >
                <ReadOnlyGroup rows={[
                    { label: 'Returns', help: 'Whether the return flow is active for this merchant.', value: returnCfg?.isActive ? 'Active' : 'Inactive', state: returnCfg?.isActive ? 'set' : 'unset' },
                    { label: 'Return updates by SMS', value: yesNo(returnCfg?.communicationTypes?.SMS), state: stateOf(returnCfg?.communicationTypes?.SMS) },
                    { label: 'Return updates by WhatsApp', value: yesNo(returnCfg?.communicationTypes?.WHATSAPP), state: stateOf(returnCfg?.communicationTypes?.WHATSAPP) },
                ]} />

                {shippingConfigs.length > 0 && (
                    <Box sx={{ pt: 1.5, borderTop: `1px solid ${C.lineSoft}` }}>
                        <Typography sx={{ ...TYPE.eyebrow, mb: 1 }}>Shipping label keys</Typography>
                        {shippingConfigs.map((c, i) => (
                            <ConfigRow
                                key={c.configKey || i}
                                dense
                                label={c.configDesc || c.configKey}
                                help={c.configKey && c.configDesc ? c.configKey : undefined}
                                state={c.enabled ? 'set' : 'unset'}
                                value={
                                    typeof c.configValue === 'string' && /^https?:\/\//.test(c.configValue)
                                        ? <a href={c.configValue} target="_blank" rel="noreferrer" style={{ color: C.accent, fontWeight: 600, fontSize: 13 }}>Open</a>
                                        : (c.configValue ?? '—')
                                }
                            />
                        ))}
                    </Box>
                )}
            </ConfigSection>

            {/* ── Shopify sync ─────────────────────────────────────────── */}
            <ConfigSection
                title="Shopify sync"
                subtitle="How shipment progress is reflected on the merchant's Shopify store."
                icon={<StorefrontOutlinedIcon />}
                state={cfg?.shopifyFulfillmentTrigger && cfg.shopifyFulfillmentTrigger !== 'AWB_GENERATED' ? 'set' : 'unset'}
            >
                <ShopifyFulfillmentTrigger id={id} config={cfg} setReload={setReload2} />
            </ConfigSection>

            {/* ── Failed deliveries & pickups ──────────────────────────── */}
            <ConfigSection
                title="Failed deliveries & pickups"
                subtitle="What happens automatically when a delivery or pickup fails. Rules are uploaded from Bulk Operations."
                icon={<PhoneInTalkOutlinedIcon />}
                state={cfg?.whatsappAutomatedNdr || cfg?.futworksNdrConfig?.push_lead ? 'set' : 'unset'}
                badge="Read-only here"
            >
                <ReadOnlyGroup rows={[
                    { label: 'WhatsApp on failed delivery', help: 'Message the buyer over WhatsApp when a delivery fails.', value: onOff(cfg?.whatsappAutomatedNdr), state: stateOf(cfg?.whatsappAutomatedNdr) },
                    { label: 'NDR managed by Prozo', help: 'Prozo’s team works this merchant’s failed deliveries.', value: onOff(cfg?.ndrManagedByProzo), state: stateOf(cfg?.ndrManagedByProzo) },
                    { label: 'Futworks calling — failed delivery', help: 'Outbound calling vendor for NDR.', value: onOff(cfg?.futworksNdrConfig?.push_lead), state: stateOf(cfg?.futworksNdrConfig?.push_lead), audit: auditOf(cfg?.futworksNdrConfig) },
                    cfg?.futworksNdrConfig?.tele_project_id && { label: 'Futworks NDR tele project ID', help: 'Identifies this merchant\u2019s campaign at the calling vendor.', value: cfg.futworksNdrConfig.tele_project_id },
                    { label: 'Futworks calling — failed pickup', help: 'Outbound calling vendor for NPR.', value: onOff(cfg?.futworksNprConfig?.push_lead), state: stateOf(cfg?.futworksNprConfig?.push_lead), audit: auditOf(cfg?.futworksNprConfig) },
                    cfg?.futworksNprConfig?.tele_project_id && { label: 'Futworks NPR tele project ID', help: 'Identifies this merchant\u2019s campaign at the calling vendor.', value: cfg.futworksNprConfig.tele_project_id },
                    { label: 'GrowupNext calling — failed delivery', value: onOff(cfg?.growupNextNdrConfig?.push_lead), state: stateOf(cfg?.growupNextNdrConfig?.push_lead), audit: auditOf(cfg?.growupNextNdrConfig) },
                    cfg?.growupNextNdrConfig?.tele_project_id && { label: 'GrowupNext tele project ID', help: 'Identifies this merchant\u2019s campaign at the calling vendor.', value: cfg.growupNextNdrConfig.tele_project_id },
                    { label: 'Skip reattempt after Futwork call', value: yesNo(cfg?.isFutworkSkipNdrReattempt), state: stateOf(cfg?.isFutworkSkipNdrReattempt) },
                ]} />
            </ConfigSection>

            {/* ── Automated reports ────────────────────────────────────── */}
            <ConfigSection
                title="Automated reports"
                subtitle="Scheduled shipment reports emailed to the merchant."
                icon={<SummarizeOutlinedIcon />}
                state={mis?.dailyEnabled || mis?.monthlyEnabled ? 'set' : 'unset'}
                stateHint={mis?.recievedByEmail?.length ? `Sent to ${mis.recievedByEmail.length} address(es)` : 'No recipient addresses configured yet'}
            >
                <ConfigToggle
                    label="Daily report"
                    help="Sends yesterday's shipment summary each morning to the merchant's registered addresses."
                    checked={!!mis?.dailyEnabled}
                    onChange={(next) => setMisFlag('daily', next)}
                    disabled={!mis?.recievedByEmail?.length}
                    disabledReason="Add at least one recipient email address before turning reports on."
                    audit={auditOf(mis)}
                />
                <ConfigToggle
                    label="Monthly report"
                    help="Sends the previous month's shipment summary on the 1st."
                    checked={!!mis?.monthlyEnabled}
                    onChange={(next) => setMisFlag('monthly', next)}
                    disabled={!mis?.recievedByEmail?.length}
                    disabledReason="Add at least one recipient email address before turning reports on."
                    audit={auditOf(mis)}
                />
                <ConfigRow
                    dense
                    label="Recipients"
                    help="Who receives the automated reports."
                    state={mis?.recievedByEmail?.length ? 'set' : 'unset'}
                    value={mis?.recievedByEmail?.length ? mis.recievedByEmail.join(', ') : '—'}
                />
                <ConfigRow dense label="B2B report (daily)" value={onOff(cfg?.b2BMISConfig?.dailyEnabled)} state={stateOf(cfg?.b2BMISConfig?.dailyEnabled)} audit={auditOf(cfg?.b2BMISConfig)} />
                <ConfigRow dense label="B2B report (monthly)" value={onOff(cfg?.b2BMISConfig?.monthlyEnabled)} state={stateOf(cfg?.b2BMISConfig?.monthlyEnabled)} audit={auditOf(cfg?.b2BMISConfig)} />
                <ConfigRow dense label="ABD B2B report (daily)" help="Appointment-before-delivery report." value={onOff(cfg?.abdb2BMisConfig?.dailyEnabled)} state={stateOf(cfg?.abdb2BMisConfig?.dailyEnabled)} audit={auditOf(cfg?.abdb2BMisConfig)} />
                <ConfigRow dense label="ABD B2B report (monthly)" value={onOff(cfg?.abdb2BMisConfig?.monthlyEnabled)} state={stateOf(cfg?.abdb2BMisConfig?.monthlyEnabled)} audit={auditOf(cfg?.abdb2BMisConfig)} />
            </ConfigSection>

            {/* ── Return QC ────────────────────────────────────────────── */}
            <ConfigSection
                title="Return quality checks"
                subtitle="Questions asked by the rider when collecting a return."
                icon={<FactCheckOutlinedIcon />}
                state={cfg?.qcListConfigs?.categoryListQc?.length ? 'set' : 'unset'}
                badge={cfg?.qcListConfigs?.categoryListQc?.length ? `${cfg.qcListConfigs.categoryListQc.length} categories` : undefined}
            >
                <Grid container sx={{ pt: 1 }}>
                    <Grid item xs={12}>
                        <ConfigList id={id} data={cfg?.qcListConfigs?.categoryListQc} />
                    </Grid>
                </Grid>
            </ConfigSection>
        </>
    );
}
